import React, { useState, useEffect, useRef, useCallback } from 'react';
import AssinaturaCanvas from '../components/AssinaturaCanvas';

const API = process.env.REACT_APP_API_URL;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
    ...opts,
  }).then(r => r.json());
}

// Garante que qualquer valor seja exibido como string segura no JSX
// Evita o React Error #31 ("Objects are not valid as a React child")
function str(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return '';  // objeto nunca vai pro JSX
  return String(v);
}

const PRIORIDADE = {
  baixa:   { label: 'Baixa',   cor: '#27ae60' },
  media:   { label: 'Média',   cor: '#f39c12' },
  alta:    { label: 'Alta',    cor: '#e67e22' },
  critica: { label: 'Crítica', cor: '#e74c3c' },
};

const STATUS_MAP = {
  aberto:             { label: 'Aberto',             bg: '#e8f5e9', color: '#1b5e20' },
  em_atendimento:     { label: 'Em Atendimento',     bg: '#e3f2fd', color: '#0d47a1' },
  aguardando_cliente: { label: 'Aguardando Cliente', bg: '#fff8e1', color: '#f57f17' },
  resolvido:          { label: 'Resolvido',          bg: '#f3e5f5', color: '#6a1b9a' },
  fechado:            { label: 'Fechado',            bg: '#eceff1', color: '#37474f' },
};

const KANBAN_COLS = [
  { key: 'aberto',             label: 'A Fazer',    cor: '#e74c3c', statuses: ['aberto'] },
  { key: 'em_atendimento',     label: 'Atendendo',  cor: '#27ae60', statuses: ['em_atendimento'] },
  { key: 'aguardando_cliente', label: 'Pausado',    cor: '#f39c12', statuses: ['aguardando_cliente', 'resolvido'] },
  { key: 'fechado',            label: 'Finalizado', cor: '#95a5a6', statuses: ['fechado'] },
];

/* ─── Visão filtrada pelos quadrados de KPI (não altera o Kanban) ───
   Cada quadrado de contador do dashboard vira um filtro clicável que troca
   o Kanban por uma grade solta de cards, em vez de filtrar as colunas. */
const FILTROS_KPI = [
  { kpi: 'aberto',             label: 'Abertos',          cor: '#e74c3c' },
  { kpi: 'em_atendimento',     label: 'Em Atendimento',   cor: '#27ae60' },
  { kpi: 'aguardando_cliente', label: 'Pausados',         cor: '#f39c12' },
  { kpi: 'sla_estourado',      label: 'SLA Estourado',    cor: '#e74c3c' },
  { kpi: 'fechado_hoje',       label: 'Finalizados hoje', cor: '#6b7a8d' },
];

function isHojeLocal(dt) {
  if (!dt) return false;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return false;
  const hoje = new Date();
  return d.getFullYear() === hoje.getFullYear() && d.getMonth() === hoje.getMonth() && d.getDate() === hoje.getDate();
}

// Mesma lógica usada para colorir as colunas do Kanban e a barra de SLA dos
// cards — mantém os contadores dos quadrados e a lista filtrada consistentes.
// OBS: "fechado_hoje" usa tkt.fechado_em quando existir, com fallback para
// tkt.data_saida (único campo de encerramento hoje exposto pelo frontend).
// Se o backend usar outro campo para calcular /tickets/dashboard, ajuste aqui.
function ticketBateFiltroKpi(t, kpi) {
  switch (kpi) {
    case 'aberto':             return t.status === 'aberto';
    case 'em_atendimento':     return t.status === 'em_atendimento';
    case 'aguardando_cliente': return t.status === 'aguardando_cliente' || t.status === 'resolvido';
    case 'sla_estourado':      return t.status !== 'fechado' && !!t.sla_prazo && new Date(t.sla_prazo) < new Date();
    case 'fechado_hoje':       return t.status === 'fechado' && isHojeLocal(t.fechado_em || t.data_saida);
    default: return false;
  }
}

function fmtTempo(s) {
  if (!s && s !== 0) return '00:00:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return [h, m, sec].map(x => String(x).padStart(2, '0')).join(':');
}

function fmtHM(s) {
  const h = Math.floor((s || 0) / 3600), m = Math.floor(((s || 0) % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function fmtDt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Converte uma data (ISO ou Date) para o formato "YYYY-MM-DDTHH:mm" exigido por <input type="datetime-local">,
// preservando o horário local (evita o input mostrar a data errada por causa do UTC).
function toDatetimeLocal(dt) {
  const d = dt ? new Date(dt) : new Date();
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Avatar({ nome, size = 32, cor }) {
  const ini = (nome || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: cor || '#1A2233', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, flexShrink: 0 }}>
      {ini}
    </div>
  );
}

function Cronometro({ ativo, segundosBase, segRef, sessaoInicioEpoch }) {
  // Calcula o valor inicial: acumulado + segundos já decorridos desde o último play
  function calcularBase() {
    const base = segundosBase || 0;
    if (ativo && sessaoInicioEpoch) {
      const agoraEpoch = Math.floor(Date.now() / 1000);
      const decorrido  = Math.max(0, agoraEpoch - Number(sessaoInicioEpoch));
      return base + decorrido;
    }
    return base;
  }

  const [seg, setSeg] = useState(calcularBase);
  const iv = useRef(null);

  // Sempre que as props mudarem (recarregou ticket), recalcula o valor base
  useEffect(() => {
    const novo = calcularBase();
    setSeg(novo);
    if (segRef) segRef.current = novo;
  }, [segundosBase, sessaoInicioEpoch, ativo]); // eslint-disable-line

  // Inicia/para o intervalo conforme ativo
  useEffect(() => {
    if (ativo) {
      iv.current = setInterval(() => {
        setSeg(s => { const n = s + 1; if (segRef) segRef.current = n; return n; });
      }, 1000);
    } else {
      clearInterval(iv.current);
    }
    return () => clearInterval(iv.current);
  }, [ativo, segRef]);

  return (
    <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: ativo ? '#27ae60' : '#6b7a8d', background: ativo ? '#e8f5e9' : '#f0f2f5', border: `1.5px solid ${ativo ? '#27ae60' : '#d4dde8'}`, borderRadius: 8, padding: '3px 10px', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {ativo && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#27ae60', animation: 'pulse 1s infinite', display: 'inline-block' }} />}
      {fmtTempo(seg)}
    </span>
  );
}

function SlaBar({ prazo }) {
  if (!prazo) return null;
  const agora = Date.now(), fim = new Date(prazo).getTime(), inicio = fim - 24 * 3600 * 1000;
  const pct = Math.min(100, Math.max(0, ((agora - inicio) / (fim - inicio)) * 100));
  const estourado = agora > fim;
  const cor = estourado ? '#e74c3c' : pct > 75 ? '#f39c12' : '#27ae60';
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ height: 4, background: '#eee', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: cor }} />
      </div>
      {estourado && <span style={{ fontSize: 10, color: '#e74c3c', fontWeight: 600 }}>SLA estourado</span>}
    </div>
  );
}

/* ─── Mini gráficos da visão filtrada (dados reais dos tickets filtrados) ─── */
const CORES_FILA = ['#D85A30', '#1A2233', '#7C8AA0', '#B84A24', '#94a3b8', '#f39c12'];

function DonutMini({ dados, size = 88 }) {
  const total = dados.reduce((s, d) => s + d.count, 0);
  const R = 40, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <circle cx="50" cy="50" r={R} fill="none" stroke="#d4dde8" strokeWidth="14" />
      {total > 0 && dados.map((d, i) => {
        if (!d.count) return null;
        const dash = (d.count / total) * C;
        const offset = -acc;
        acc += dash;
        return (
          <circle key={i} cx="50" cy="50" r={R} fill="none" stroke={d.cor} strokeWidth="14"
            strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={offset} transform="rotate(-90 50 50)" />
        );
      })}
    </svg>
  );
}

function BarrasHorizontais({ dados }) {
  const max = Math.max(1, ...dados.map(d => d.count));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {dados.map(d => (
        <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 56, fontSize: 11, color: '#6b7a8d', flexShrink: 0 }}>{d.label}</span>
          <div style={{ flex: 1, height: 9, background: '#f5f7fa', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ width: `${(d.count / max) * 100}%`, height: '100%', background: d.cor }} />
          </div>
          <span style={{ width: 18, fontSize: 11, fontWeight: 700, color: '#1A2233', textAlign: 'right', flexShrink: 0 }}>{d.count}</span>
        </div>
      ))}
    </div>
  );
}

// Agrupa a lista filtrada em: contagem por fila, contagem por prioridade e o
// tempo médio de atraso (só entre os tickets com SLA já vencido na lista).
function estatisticasFiltro(lista) {
  const porFilaMap = {};
  lista.forEach(t => { const f = t.fila || 'Sem fila'; porFilaMap[f] = (porFilaMap[f] || 0) + 1; });
  const porFila = Object.entries(porFilaMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([label, count], i) => ({ label, count, cor: CORES_FILA[i % CORES_FILA.length] }));

  const porPrioridade = Object.entries(PRIORIDADE).map(([k, v]) => ({
    label: v.label, cor: v.cor, count: lista.filter(t => t.prioridade === k).length,
  }));

  const atrasados = lista.filter(t => t.sla_prazo && new Date(t.sla_prazo) < new Date());
  let atrasoMedioTexto = null;
  if (atrasados.length > 0) {
    const mediaMin = Math.floor(atrasados.reduce((s, t) => s + (Date.now() - new Date(t.sla_prazo).getTime()), 0) / atrasados.length / 60000);
    atrasoMedioTexto = `${Math.floor(mediaMin / 60)}h ${String(mediaMin % 60).padStart(2, '0')}min`;
  }

  return { porFila, porPrioridade, atrasoMedioTexto, totalAtrasados: atrasados.length };
}

/* ─── Busca de cliente cadastrado (por nome ou CNPJ) — usada no Novo Ticket e no popup ─── */
function ClienteBusca({ clientes, selecionado, onSelect, placeholder }) {
  const [termo, setTermo]   = useState('');
  const [aberto, setAberto] = useState(false);

  const termoLower  = termo.trim().toLowerCase();
  const termoDigits = termo.replace(/\D/g, '');
  const filtrados = termoLower
    ? clientes.filter(c =>
        (c.nome || '').toLowerCase().includes(termoLower) ||
        (c.cliente_apelido || '').toLowerCase().includes(termoLower) ||
        (termoDigits && (c.cnpj || '').replace(/\D/g, '').includes(termoDigits))
      ).slice(0, 20)
    : [];

  if (selecionado) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d4dde8', borderRadius: 7, padding: '7px 10px', background: '#f8fafc' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1A2233', flex: 1 }}>&#127970; {str(selecionado.nome)}</span>
        <button type="button" onClick={() => onSelect(null)}
          style={{ fontSize: 11, color: '#e74c3c', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>
          × trocar
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={termo}
        onChange={e => { setTermo(e.target.value); setAberto(true); }}
        onFocus={() => setAberto(true)}
        onBlur={() => setTimeout(() => setAberto(false), 150)}
        placeholder={placeholder || 'Buscar por nome ou CNPJ...'}
        style={{ border: '1px solid #d4dde8', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
      />
      {aberto && termoLower && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d4dde8', borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: 'auto', zIndex: 20, boxShadow: '0 6px 20px rgba(0,0,0,.12)' }}>
          {filtrados.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: '#94a3b8' }}>Nenhum cliente encontrado.</div>
          )}
          {filtrados.map(c => (
            <div key={c.id}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onSelect(c); setTermo(''); setAberto(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f3f7', fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: '#1A2233' }}>{str(c.nome)}{c.cliente_apelido ? ` (${str(c.cliente_apelido)})` : ''}</div>
              {c.cnpj && <div style={{ fontSize: 11, color: '#94a3b8' }}>{str(c.cnpj)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── PDF / OS com identidade visual do backend (igual ao orçamento) ─── */
function gerarHTMLOS(tkt, msgs, configs, checklistRespostas = [], camposCustomDefs = [], labelsCustom = {}) {
  function cfg(chave) {
    if (Array.isArray(configs)) { const i = configs.find(c => c.chave === chave); return i ? i.valor : ''; }
    return (configs && configs[chave]) || '';
  }
  const logo  = cfg('empresa_logo');
  const logoH = cfg('empresa_logo_altura') || 60;
  const cor   = cfg('empresa_cor') || '#1A2233';
  const nome  = cfg('empresa_nome') || '';
  const cnpj  = cfg('empresa_cnpj') || '';
  const end   = cfg('empresa_endereco') || '';
  const tel   = cfg('empresa_telefone') || '';
  const site  = cfg('empresa_site') || '';

  const esc   = s => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const fmtOS = dt => dt ? new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtS  = s => { const h = Math.floor((s||0)/3600), m = Math.floor(((s||0)%3600)/60), sec = (s||0)%60; return [h,m,sec].map(x=>String(x).padStart(2,'0')).join(':'); };
  const lbl   = (chave, padrao) => esc(labelsCustom?.[chave] || padrao);

  // Mesma regra do backend (gerarHTMLOS em tickets.js): nota interna e histórico de
  // chat copiado não entram na OS impressa/PDF — só as movimentações reais do ticket.
  const extraiImagem = c => { const m = /^\[imagem:(.+)\]$/.exec(c || ''); return m ? m[1] : null; };
  const acomp = (msgs||[])
    .filter(m => m.origem !== 'sistema' && m.origem !== 'interno' && m.tipo !== 'chat_historico')
    .map(m => {
      const urlImg = m.tipo === 'imagem' ? extraiImagem(m.conteudo) : null;
      const conteudoHtml = urlImg
        ? `<img src="${esc(urlImg)}" style="max-width:220px;max-height:220px;border-radius:6px;display:block">`
        : `<span style="white-space:pre-wrap">${esc(m.conteudo||'')}</span>`;
      return `<tr><td style="white-space:nowrap">${fmtOS(m.criado_em)}</td><td>${esc(m.usuario_nome||'—')}</td><td>${conteudoHtml}</td></tr>`;
    }).join('');

  const camposCustomValores = tkt.campos_customizados || {};
  const camposCustomPreenchidos = (camposCustomDefs || []).filter(
    d => camposCustomValores[d.nome_interno] !== undefined && camposCustomValores[d.nome_interno] !== ''
  );

  const blocoAssinatura = (base64, nomeFallback) => `
    <div style="flex:1;text-align:center">
      ${base64 ? `<img src="${base64}" style="max-height:60px;max-width:220px;display:block;margin:0 auto 4px">` : '<div style="height:60px"></div>'}
      <div class="ab">${esc(nomeFallback)}</div>
    </div>`;

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>OS #${tkt.numero || tkt.id}</title>
<style>
  *{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;color:#222;margin:0;padding:16px}
  @page{margin:12mm}
  .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${cor};padding-bottom:14px;margin-bottom:20px}
  .ei{font-size:10.5px;color:#555;line-height:1.7;text-align:right}
  .ot{font-size:20px;font-weight:900;color:${cor}}.os{font-size:11px;color:#888;margin-top:2px}
  .sec{margin:10px 0}.st{font-weight:700;font-size:11px;color:${cor};border-bottom:1.5px solid ${cor};padding-bottom:4px;margin-bottom:10px;text-transform:uppercase;letter-spacing:.5px}
  .campos{display:flex;flex-wrap:wrap;gap:10px 28px}
  .campo .cl{font-size:9.5px;color:#888;text-transform:uppercase;font-weight:700;display:block;margin-bottom:2px}.campo .cv{font-size:11.5px;font-weight:600}
  table{width:100%;border-collapse:collapse;margin:6px 0;page-break-inside:auto}
  thead{display:table-header-group}
  tr{page-break-inside:avoid;page-break-after:auto}
  th{background:${cor};color:#fff;padding:6px 10px;text-align:left;font-size:10px;text-transform:uppercase}
  td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px;vertical-align:top}tr:nth-child(even) td{background:#fafafa}
  .db{background:#f8fafc;border:1px solid #e2e8f0;border-radius:5px;padding:10px 12px;font-size:11.5px;line-height:1.65;white-space:pre-wrap}
  .rb{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:5px;padding:10px 12px;font-size:11.5px;line-height:1.65;white-space:pre-wrap}
  .aa{display:flex;gap:40px;margin-top:22px;page-break-inside:avoid}.ab{border-top:1px solid #333;margin-top:8px;padding-top:6px;font-size:10px;color:#666;text-align:center}
  @media print{body{padding:0}}
</style></head><body>
<div class="hd">
  <div>${logo ? `<img src="${logo}" style="height:${logoH}px;max-width:200px;object-fit:contain;display:block">` : `<div class="ot">${esc(nome)}</div>`}</div>
  <div class="ei">${nome?`<strong>${esc(nome)}</strong><br>`:''} ${cnpj?`CNPJ: ${esc(cnpj)}<br>`:''} ${tel?`Tel: ${esc(tel)}<br>`:''} ${site?`${esc(site)}<br>`:''} ${end?esc(end):''}</div>
</div>
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px">
  <div><div class="ot">Ordem de Servico #${tkt.numero || tkt.id}</div><div class="os">Emitida em ${fmtOS(new Date())}</div></div>
  <div style="font-size:11px;font-weight:700;color:${cor};background:${cor}18;border:1px solid ${cor}40;padding:3px 12px;border-radius:20px">${tkt.status==='fechado'?'Concluida':tkt.status==='em_atendimento'?'Em Atendimento':'Em Aberto'}</div>
</div>
<div class="sec"><div class="st">Dados do Solicitante</div>
  <div class="campos">
    <div class="campo"><span class="cl">${lbl('cliente_nome','Nome')}</span><span class="cv">${esc(tkt.cliente_nome)||'—'}</span></div>
    ${tkt.cliente_telefone?`<div class="campo"><span class="cl">${lbl('cliente_telefone','Telefone')}</span><span class="cv">${esc(tkt.cliente_telefone)}</span></div>`:''}
    ${tkt.cliente_email?`<div class="campo"><span class="cl">${lbl('cliente_email','E-mail')}</span><span class="cv">${esc(tkt.cliente_email)}</span></div>`:''}
  </div>
</div>
<div class="sec"><div class="st">Dados do Ticket</div>
  <div class="campos">
    <div class="campo"><span class="cl">${lbl('titulo','Assunto')}</span><span class="cv">${esc(tkt.titulo)||'—'}</span></div>
    <div class="campo"><span class="cl">${lbl('prioridade','Prioridade')}</span><span class="cv">${esc(tkt.prioridade)||'—'}</span></div>
    <div class="campo"><span class="cl">${lbl('fila','Fila')}</span><span class="cv">${esc(tkt.fila)||'—'}</span></div>
    <div class="campo"><span class="cl">${lbl('origem','Origem')}</span><span class="cv">${esc(tkt.origem)||'manual'}</span></div>
    ${camposCustomPreenchidos.map(d => `<div class="campo"><span class="cl">${esc(d.label)}</span><span class="cv">${esc(String(camposCustomValores[d.nome_interno]))}</span></div>`).join('')}
  </div>
</div>
${tkt.descricao?`<div class="sec"><div class="st">Descricao da Solicitacao</div><div class="db">${esc(tkt.descricao)}</div></div>`:''}
<div class="sec"><div class="st">Acompanhamentos</div>
  <table><thead><tr><th style="width:130px">Data/Hora</th><th style="width:130px">Responsavel</th><th>Descricao</th></tr></thead>
  <tbody>${acomp||'<tr><td colspan="3" style="text-align:center;color:#aaa;padding:14px">Sem acompanhamentos</td></tr>'}</tbody></table>
</div>
<div class="sec"><div class="st">Informacoes de Atendimento</div>
  <table><thead><tr><th>${lbl('atendente_nome','Tecnico')}</th><th>Criacao</th><th>SLA</th><th>Chegada</th><th>Saida</th><th>Tempo Total</th><th>Tipo</th></tr></thead>
  <tbody><tr><td>${esc(tkt.atendente_nome||'—')}</td><td>${fmtOS(tkt.criado_em)}</td><td>${fmtOS(tkt.sla_prazo)}</td><td>${fmtOS(tkt.data_chegada)}</td><td>${fmtOS(tkt.data_saida)}</td><td style="font-family:monospace;font-weight:700">${fmtS(tkt.tempo_total_segundos)}</td><td>${tkt.tipo_atendimento==='externo'?'Externo':'Interno'}</td></tr></tbody></table>
</div>
${tkt.comentario_finalizacao?`<div class="sec"><div class="st">Resolucao</div><div class="rb">${esc(tkt.comentario_finalizacao)}</div></div>`:''}
${(checklistRespostas||[]).length>0?`
<div class="sec"><div class="st">Checklist de Finalizacao</div>
${checklistRespostas.map(cl=>`
  <div style="margin-bottom:8px">
    <div style="font-weight:700;font-size:11.5px;color:#333;margin-bottom:6px;padding:5px 8px;background:#f8fafc;border-left:3px solid ${cor};border-radius:2px">${esc(cl.nome)}</div>
    <table style="margin:0"><thead><tr><th style="width:40%">Item</th><th style="width:60%">Resposta</th></tr></thead><tbody>
    ${(cl.itens||[]).map((it,i)=>{
      const tipo = it.tipo||'checkbox';
      let resposta = '';
      if(tipo==='checkbox') resposta = it.respondido ? '✓ Confirmado' : '✗ Não confirmado';
      else if(tipo==='multipla') resposta = it.resposta_texto || '—';
      else resposta = it.resposta_texto || '—';
      const bg = i%2===0?'':'background:#fafafa';
      const cor_resp = tipo==='checkbox'?(it.respondido?'color:#15803d;font-weight:700':'color:#dc2626;font-weight:700'):'color:#1A2233';
      return `<tr style="${bg}"><td style="font-size:10.5px">${esc(it.descricao)}</td><td style="font-size:10.5px;${cor_resp}">${esc(resposta)}</td></tr>`;
    }).join('')}
    </tbody></table>
  </div>
`).join('')}
</div>`:''}
<div class="aa">
  ${blocoAssinatura(tkt.assinatura_tecnico, tkt.atendente_nome || lbl('atendente_nome','Tecnico Responsavel'))}
  ${blocoAssinatura(tkt.assinatura_cliente, tkt.cliente_nome || lbl('cliente_nome','Cliente / Solicitante'))}
</div>
</body></html>`;
}

/* ═══════ POPUP DETALHE DO TICKET ═══════ */
function TicketPopup({ ticket: tktInit, perfis, usuarios, tipos, categorias, mesas, onClose, onUpdate, onDelete }) {
  const [tkt, setTkt]               = useState(tktInit);
  const [msgs, setMsgs]             = useState([]);
  const [sessoes, setSessoes]       = useState([]);
  const [novaMsg, setNovaMsg]       = useState('');
  const [tipoMsg, setTipoMsg]       = useState('interno');
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando]     = useState(false);
  const [configs, setConfigs]       = useState({});
  const [visualizadores, setVis]    = useState([]);
  const [subcats, setSubcats]       = useState([]);

  // ── Anexo de foto na movimentação (Nota interna / Publico / Atendente) ──
  const [anexoPendente, setAnexoPendente] = useState(null); // { base64, mimeType, previewUrl }
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const [erroAnexo, setErroAnexo]         = useState('');
  const fileInputRef = useRef(null);

  // ── Aba inferior: timeline de movimentações ou galeria de fotos ──
  const [abaInferior, setAbaInferior] = useState('timeline'); // 'timeline' | 'fotos'
  const [fotoAmpliada, setFotoAmpliada] = useState(null); // { url, index } | null

  const [modalPausar,     setModalPausar]     = useState(false);
  const [motivoPausa,     setMotivoPausa]     = useState('');
  const [modalFinalizar,  setModalFinalizar]  = useState(false);
  const [erroFinalizar,   setErroFinalizar]   = useState('');
  const [comentFinal,     setComentFinal]     = useState('');
  const [tipoAtend,       setTipoAtend]       = useState('interno');
  const [tempoFinal,      setTempoFinal]      = useState('00:00');
  const [dataAbertura,    setDataAbertura]    = useState(''); // datetime-local — criado_em, editável só no modal Finalizar
  const [dataFinalizacao, setDataFinalizacao] = useState(''); // datetime-local — fechado_em, editável só no modal Finalizar
  const [modalTransferir, setModalTransferir] = useState(false);
  const [transMode,       setTransMode]       = useState('tecnico');
  const [transTecnico,    setTransTecnico]    = useState('');
  const [transFila,       setTransFila]       = useState('');

  // ── Ordem de Serviço: rótulos, campos customizados e assinaturas ──
  const [labelsCustom, setLabelsCustom]           = useState({});
  const [camposCustomDefs, setCamposCustomDefs]   = useState([]);
  const [assinaturaTecnico, setAssinaturaTecnico] = useState(null); // base64 PNG capturado no modal de finalizar
  const [assinaturaCliente, setAssinaturaCliente] = useState(null);
  const assinaturaAtiva = configs?.tkt_assinatura_ativa === 'true';

  // ── Vincular cliente a um ticket que ainda não tem (ex: criado manualmente) ──
  const [vincQuery, setVincQuery]         = useState('');
  const [vincResultados, setVincResultados] = useState([]);
  const [vincBuscando, setVincBuscando]   = useState(false);
  const [vincDropOpen, setVincDropOpen]   = useState(false);
  const [vincSalvando, setVincSalvando]   = useState(false);
  const vincDebounce = useRef(null);

  function buscarVincular(q) {
    setVincQuery(q);
    setVincDropOpen(true);
    if (vincDebounce.current) clearTimeout(vincDebounce.current);
    if (q.trim().length < 2) { setVincResultados([]); return; }
    vincDebounce.current = setTimeout(async () => {
      setVincBuscando(true);
      try {
        const r = await api(`/tickets/clientes/buscar?q=${encodeURIComponent(q.trim())}`);
        setVincResultados(Array.isArray(r) ? r : []);
      } catch { setVincResultados([]); }
      setVincBuscando(false);
    }, 350);
  }

  async function vincularClienteTicket(cli) {
    setVincSalvando(true);
    try {
      const r = await api(`/tickets/${tkt.id}`, { method: 'PATCH', body: JSON.stringify({ cliente_id: cli.id }) });
      if (r?.erro) { alert(r.erro); setVincSalvando(false); return; }
      setTkt(p => ({ ...p, cliente_id: cli.id, empresa_nome: cli.nome }));
      setVincQuery(''); setVincResultados([]); setVincDropOpen(false);
      onUpdate?.();
    } catch { alert('Erro ao vincular cliente.'); }
    setVincSalvando(false);
  }

  // ── Checklist obrigatório ──
  const [checklistModal,    setChecklistModal]    = useState(null);
  // { checklists: [...], respostas: { checklistId: { itemId: bool } } }
  const [checklistRespostas, setChecklistRespostas] = useState([]); // para o PDF

  const tlRef    = useRef(null);
  const segRef   = useRef(tktInit.tempo_total_segundos || 0);
  const cronAtivo = tkt.status === 'em_atendimento';
  const isFechado = tkt.status === 'fechado';
  const isGestor  = perfis.some(p => p.toLowerCase() === 'ges');

  /* busca configs da empresa para PDF — mesma rota do orçamento */
  useEffect(() => {
    api('/configuracoes').then(d => {
      if (!d) return;
      const obj = Array.isArray(d) ? d.reduce((a, c) => { a[c.chave] = c.valor; return a; }, {}) : (typeof d === 'object' ? d : {});
      setConfigs(obj);
    }).catch(() => {});
    api('/tickets/labels').then(d => { if (d && !d.erro) setLabelsCustom(d); }).catch(() => {});
    api('/tickets/campos-customizados').then(d => { if (Array.isArray(d)) setCamposCustomDefs(d.filter(c => c.ativo !== false)); }).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const d = await api(`/tickets/${tkt.id}`);
      if (d && d.id) {
        setTkt(d); setMsgs(d.mensagens || []); setVis(d.visualizadores || []);
        segRef.current = d.tempo_total_segundos || 0;
        setSubcats(categorias.find(c => c.id === d.categoria_id)?.subcategorias || []);
      }
      const s = await api(`/tickets/${tkt.id}/sessoes`);
      if (Array.isArray(s)) setSessoes(s);
      // Carrega respostas do checklist para o PDF
      try {
        const cr = await api(`/tickets/checklists/respostas/${tkt.id}`);
        if (Array.isArray(cr)) setChecklistRespostas(cr);
      } catch (_) {}
    } catch (_) {}
    setCarregando(false);
  }, [tkt.id, categorias]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { api(`/tickets/${tkt.id}/visualizar`, { method: 'POST' }).catch(() => {}); }, [tkt.id]);
  useEffect(() => { if (tlRef.current) tlRef.current.scrollTop = tlRef.current.scrollHeight; }, [msgs]);
  useEffect(() => { setSubcats(categorias.find(c => c.id === tkt.categoria_id)?.subcategorias || []); }, [tkt.categoria_id, categorias]);

  async function salvarCampo(campo, valor) {
    await api(`/tickets/${tkt.id}`, { method: 'PATCH', body: JSON.stringify({ [campo]: valor }) });
    setTkt(p => ({ ...p, [campo]: valor })); onUpdate?.();
  }

  async function salvarCampoCustomizado(nomeInterno, valor) {
    const novosValores = { ...(tkt.campos_customizados || {}), [nomeInterno]: valor };
    await api(`/tickets/${tkt.id}`, { method: 'PATCH', body: JSON.stringify({ campos_customizados: { [nomeInterno]: valor } }) });
    setTkt(p => ({ ...p, campos_customizados: novosValores }));
  }

  async function enviarMovimentacao() {
    if (!novaMsg.trim() && !anexoPendente) return;
    setEnviando(true);
    if (novaMsg.trim()) {
      await api(`/tickets/${tkt.id}/mensagens`, { method: 'POST', body: JSON.stringify({ conteudo: novaMsg.trim(), origem: tipoMsg }) });
    }
    if (anexoPendente) {
      await enviarAnexoPendente();
    }
    setNovaMsg(''); await carregar(); setEnviando(false);
  }

  const FORMATOS_FOTO_ACEITOS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  const TAMANHO_MAX_FOTO = 500 * 1024; // 500KB — mesmo limite usado em logo/assinatura no resto do sistema

  function selecionarFoto(e) {
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // permite selecionar o mesmo arquivo de novo depois
    if (!arquivo) return;
    setErroAnexo('');
    if (!FORMATOS_FOTO_ACEITOS.includes(arquivo.type)) {
      setErroAnexo('Formato inválido. Use PNG, JPEG ou WebP.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX_FOTO) {
      setErroAnexo('Imagem maior que 500KB.');
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      setAnexoPendente({ base64: leitor.result, mimeType: arquivo.type, previewUrl: leitor.result });
    };
    leitor.readAsDataURL(arquivo);
  }

  function removerAnexoPendente() { setAnexoPendente(null); setErroAnexo(''); }

  async function enviarAnexoPendente() {
    if (!anexoPendente) return;
    setEnviandoAnexo(true);
    try {
      await api(`/tickets/${tkt.id}/mensagens/imagem`, {
        method: 'POST',
        body: JSON.stringify({ arquivo_base64: anexoPendente.base64, mime_type: anexoPendente.mimeType, origem: tipoMsg }),
      });
      setAnexoPendente(null);
    } catch (err) {
      setErroAnexo('Falha ao enviar a imagem. Tente novamente.');
    }
    setEnviandoAnexo(false);
  }

  // Extrai a URL de uma mensagem no formato "[imagem:URL]" — mesma convenção usada no Chat WhatsApp
  function urlImagemMsg(m) {
    if (m.tipo !== 'imagem') return null;
    const match = /^\[imagem:(.+)\]$/.exec(m.conteudo || '');
    return match ? match[1] : null;
  }

  const fotosDoTicket = msgs.filter(m => urlImagemMsg(m));

  async function play() { await api(`/tickets/${tkt.id}/play`, { method: 'POST' }); await carregar(); onUpdate?.(); }

  async function pausar() {
    await api(`/tickets/${tkt.id}/pausar`, { method: 'POST', body: JSON.stringify({ motivo: motivoPausa }) });
    setModalPausar(false); setMotivoPausa(''); await carregar(); onUpdate?.();
  }

  /* abre finalizar: verifica checklist da subcategoria primeiro */
  async function abrirFinalizar() {
    setTempoFinal(fmtHM(segRef.current));
    setComentFinal(''); setTipoAtend('interno'); setErroFinalizar('');
    setAssinaturaTecnico(null); setAssinaturaCliente(null);
    setDataAbertura(toDatetimeLocal(tkt.criado_em));
    setDataFinalizacao(toDatetimeLocal(new Date()));

    if (tkt.subcategoria_id) {
      try {
        const cls = await api(`/tickets/checklists/subcategoria/${tkt.subcategoria_id}`);
        if (Array.isArray(cls) && cls.length > 0) {
          // Monta estado inicial de respostas por tipo
          const respostas = {};
          cls.forEach(cl => {
            respostas[cl.id] = {};
            (cl.itens || []).forEach(item => {
              const tipo = item.tipo || 'checkbox';
              respostas[cl.id][item.id] = tipo === 'checkbox' ? false : '';
            });
          });
          setChecklistModal({ checklists: cls, respostas });
          return; // não abre modal de finalizar ainda
        }
      } catch (_) {}
    }
    // Sem checklist → abre direto
    setModalFinalizar(true);
  }

  /* confirma checklist e abre modal de finalizar */
  async function confirmarChecklist() {
    const { checklists, respostas } = checklistModal;

    // Valida que todos os itens foram respondidos conforme o tipo
    for (const cl of checklists) {
      for (const item of (cl.itens || [])) {
        const resp = respostas[cl.id]?.[item.id];
        const tipo = item.tipo || 'checkbox';
        const vazio =
          tipo === 'checkbox'      ? (resp !== true) :
          tipo === 'multipla'      ? (!resp || resp === '') :
          tipo === 'dissertativa'  ? (!resp || !String(resp).trim()) :
          !resp;
        if (vazio) {
          alert(`Responda todos os itens do checklist "${cl.nome}" antes de finalizar.`);
          return;
        }
      }
    }

    try {
      // Salva respostas no backend
      for (const cl of checklists) {
        const r = (cl.itens || []).map(item => {
          const tipo = item.tipo || 'checkbox';
          const resp = respostas[cl.id]?.[item.id];
          return {
            item_id: item.id,
            respondido: tipo === 'checkbox' ? !!resp : true,
            resposta_texto: tipo !== 'checkbox' ? String(resp || '') : null,
          };
        });
        await api(`/tickets/checklists/respostas`, {
          method: 'POST',
          body: JSON.stringify({ ticket_id: tkt.id, checklist_id: cl.id, respostas: r }),
        });
      }
    } catch (_) {}

    setChecklistModal(null);
    setModalFinalizar(true);
  }

  async function finalizar() {
    const [h, m] = tempoFinal.split(':').map(Number);
    setErroFinalizar('');
    const r = await api(`/tickets/${tkt.id}/finalizar`, { method: 'POST', body: JSON.stringify({
      comentario: comentFinal, tipo_atendimento: tipoAtend, tempo_segundos: ((h||0)*3600)+((m||0)*60),
      assinatura_tecnico: assinaturaTecnico || undefined,
      assinatura_cliente: assinaturaCliente || undefined,
      criado_em: dataAbertura ? new Date(dataAbertura).toISOString() : undefined,
      fechado_em: dataFinalizacao ? new Date(dataFinalizacao).toISOString() : undefined,
    }) });
    if (r?.erro) { setErroFinalizar(r.erro); return; } // mantém o modal aberto pra corrigir e tentar de novo
    setModalFinalizar(false); onUpdate?.(); onClose(); /* fecha popup ao finalizar */
  }

  async function reabrir() {
    if (!window.confirm('Reabrir este ticket?')) return;
    await api(`/tickets/${tkt.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'aberto' }) });
    await api(`/tickets/${tkt.id}/mensagens`, { method: 'POST', body: JSON.stringify({ conteudo: 'Ticket reaberto.', origem: 'sistema' }) });
    await carregar(); onUpdate?.();
  }

  async function transferir() {
    const body = transMode === 'tecnico' ? { atendente_id: transTecnico } : { fila: transFila, atendente_id: null, status: 'aberto' };
    await api(`/tickets/${tkt.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    await api(`/tickets/${tkt.id}/mensagens`, { method: 'POST', body: JSON.stringify({ conteudo: transMode === 'tecnico' ? `Ticket transferido para ${usuarios.find(u => u.id == transTecnico)?.nome || transTecnico}` : `Ticket transferido para a fila "${transFila}"`, origem: 'sistema' }) });
    setModalTransferir(false); await carregar(); onUpdate?.();
  }

  async function excluir() {
    if (!window.confirm('Excluir este ticket permanentemente? Esta acao nao pode ser desfeita.')) return;
    await api(`/tickets/${tkt.id}`, { method: 'DELETE' }); onDelete?.(); onClose();
  }

  function imprimir() { const w = window.open('', '_blank'); w.document.write(gerarHTMLOS(tkt, msgs, configs, checklistRespostas, camposCustomDefs, labelsCustom)); w.document.close(); setTimeout(() => w.print(), 400); }

  const statusAtual = STATUS_MAP[tkt.status] || STATUS_MAP.aberto;
  const catAtual    = categorias.find(c => c.id === tkt.categoria_id);
  const subcatAtual = subcats.find(s => s.id === tkt.subcategoria_id);
  const mesaAtual   = mesas.find(m => m.id === tkt.mesa_id);
  const operAtual   = usuarios.find(u => u.id === tkt.atendente_id);

  return (
    <>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
        @keyframes slideUp{from{transform:translateY(40px);opacity:0}to{transform:translateY(0);opacity:1}}
        .tov{position:fixed;inset:0;background:rgba(10,14,26,.76);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px;backdrop-filter:blur(3px)}
        .tpop{background:#fff;border-radius:14px;width:100%;max-width:1180px;height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:slideUp .22s ease;box-shadow:0 24px 80px rgba(0,0,0,.3)}
        .thd{background:#1A2233;color:#fff;padding:10px 14px;display:flex;flex-direction:column;gap:6px;flex-shrink:0}
        .tbody{display:flex;flex:1;overflow:hidden}
        .tleft{flex:1;display:flex;flex-direction:column;overflow:hidden;border-right:1px solid #e8ecf0}
        .tright{width:300px;overflow-y:auto;background:#f8fafc;flex-shrink:0}
        .ttl{flex:1;overflow-y:auto;padding:18px 20px 10px}
        .tinp{border-top:1px solid #e8ecf0;padding:12px 16px;background:#fff;flex-shrink:0}
        .thd-btns-mobile{display:none}
        @media(max-width:700px){
          .tov{padding:0 !important;align-items:flex-end !important}
          .tpop{border-radius:14px 14px 0 0 !important;height:96svh !important;max-width:100vw !important}
          .tbody{flex-direction:column !important;overflow-y:auto !important}
          .tleft{overflow:visible !important;border-right:none !important;border-bottom:1px solid #e8ecf0 !important;flex-shrink:0 !important}
          .ttl{overflow-y:visible !important;flex:none !important}
          .tinp{position:sticky !important;bottom:0 !important;z-index:10 !important}
          .tright{width:100% !important;overflow-y:visible !important;flex-shrink:0 !important}
          .bh-txt{display:none}
          .thd-btns-mobile{display:inline}
          .bh{padding:5px 8px !important;font-size:13px !important}
          .cr{padding:8px 12px !important}
        }
        .mitem{display:flex;gap:10px;margin-bottom:16px;align-items:flex-start}
        .mbub{border-radius:10px;padding:10px 14px;font-size:13.5px;line-height:1.55;word-break:break-word}
        .mint{background:#fffbeb;border:1px solid #fde68a}
        .mpub{background:#f0f9ff;border:1px solid #bae6fd}
        .mat{background:#fff0f0;border:1px solid #fca5a5}
        .mhist{background:#f0fdf4;border:1px solid #bbf7d0}
        .mmeta{font-size:11px;color:#94a3b8;margin-top:5px;display:flex;gap:8px}
        .cr{padding:12px 16px;border-bottom:1px solid #e8ecf0}
        .cr label{font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;display:block;margin-bottom:6px}
        .cr select{width:100%;border:1px solid #d4dde8;border-radius:7px;padding:7px 10px;font-size:13px;background:#fff;color:#1A2233;outline:none}
        .cr select:focus{border-color:#D85A30}
        .bh{border:none;cursor:pointer;border-radius:8px;padding:6px 12px;font-size:12.5px;font-weight:600;display:flex;align-items:center;gap:5px;transition:filter .15s;white-space:nowrap;flex-shrink:0}
        .bh:hover{filter:brightness(.88)}
        .tabt{border:none;background:none;padding:5px 10px;cursor:pointer;font-size:13px;border-radius:6px;transition:all .15s}
        .tabt.on{background:#1A2233;color:#fff;font-weight:600}
        .tabt:not(.on){color:#6b7a8d}
        .vrow{display:flex;align-items:center;gap:6px;padding:4px 0;font-size:12.5px}
        .srow{display:flex;justify-content:space-between;font-size:11px;color:#6b7a8d;padding:3px 0;border-bottom:1px solid #f0f2f5}
        .bdg{font-size:10px;border-radius:4px;padding:1px 6px;font-weight:700;flex-shrink:0}
        .sbox{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;margin-bottom:18px}
        .drow{display:flex;justify-content:space-between;font-size:12px;padding:3px 0;border-bottom:1px solid #f0f2f5}
      `}</style>

      <div className="tov" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="tpop">

          {/* HEADER */}
          <div className="thd">
            {/* Linha 1: voltar + título + status */}
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <button onClick={onClose} style={{background:'rgba(255,255,255,.12)',border:'none',color:'#fff',borderRadius:8,width:30,height:30,cursor:'pointer',fontSize:15,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>←</button>
              <div style={{flex:1,minWidth:0,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',gap:5}}>
                  <span style={{fontSize:11,color:'#94a3b8',flexShrink:0}}>#{str(tkt.numero)||str(tkt.id)}</span>
                  <span style={{fontSize:13,fontWeight:700,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{str(tkt.titulo)}</span>
                  <span style={{padding:'1px 7px',borderRadius:20,fontSize:10,fontWeight:700,background:statusAtual.bg,color:statusAtual.color,flexShrink:0,whiteSpace:'nowrap'}}>{statusAtual.label}</span>
                </div>
                <div style={{fontSize:10,color:'#94a3b8',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {str(tkt.cliente_nome)||'—'}{tkt.sla_prazo?` · SLA: ${fmtDt(tkt.sla_prazo)}`:''}
                </div>
              </div>
            </div>
            {/* Linha 2: cronômetro + botões (só ícone no mobile, ícone+texto no desktop) */}
            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
              <Cronometro ativo={cronAtivo} segundosBase={tkt.tempo_total_segundos||0} segRef={segRef} sessaoInicioEpoch={tkt.sessao_inicio_epoch}/>
              {!cronAtivo&&!isFechado&&<button className="bh" style={{background:'#27ae60',color:'#fff'}} onClick={play} title="Play">▶<span className="bh-txt"> Play</span></button>}
              {cronAtivo&&<button className="bh" style={{background:'#f39c12',color:'#fff'}} onClick={()=>setModalPausar(true)} title="Pausar">⏸<span className="bh-txt"> Pausar</span></button>}
              <button className="bh" style={{background:'rgba(255,255,255,.12)',color:'#fff'}} onClick={()=>setModalTransferir(true)} title="Transferir">↔<span className="bh-txt"> Transferir</span></button>
              <button className="bh" style={{background:'rgba(255,255,255,.12)',color:'#fff'}} onClick={imprimir} title="Imprimir">🖨<span className="bh-txt"> Imprimir</span></button>
              {!isFechado&&<button className="bh" style={{background:'#D85A30',color:'#fff'}} onClick={abrirFinalizar} title="Finalizar">✓<span className="bh-txt"> Finalizar</span></button>}
              {isFechado&&<button className="bh" style={{background:'#27ae60',color:'#fff'}} onClick={reabrir} title="Reabrir">↩<span className="bh-txt"> Reabrir</span></button>}
              {isGestor&&<button className="bh" style={{background:'#e74c3c',color:'#fff'}} onClick={excluir} title="Excluir">🗑<span className="bh-txt"> Excluir</span></button>}
            </div>
          </div>

          {/* BODY */}
          <div className="tbody">

            {/* ESQUERDA */}
            <div className="tleft">
              <div className="ttl" ref={tlRef}>
                {carregando && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>Carregando...</div>}

                {/* Bloco de solicitacao — dados do cliente + descricao */}
                {!carregando && (
                  <div className="sbox">
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>Solicitacao</div>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12, position: 'relative' }}>
                      {tkt.empresa_nome ? (
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>CLIENTE</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233' }}>&#127970; {str(tkt.empresa_nome)}</span>
                            <span title="Cliente vinculado" style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                              🔗 Vinculado
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ width: '100%', maxWidth: 320 }}>
                          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>CLIENTE — NÃO VINCULADO</div>
                          <input value={vincQuery}
                            onChange={e => buscarVincular(e.target.value)}
                            onFocus={() => setVincDropOpen(true)}
                            onBlur={() => setTimeout(() => setVincDropOpen(false), 150)}
                            placeholder={vincSalvando ? 'Vinculando...' : 'Buscar cliente por nome ou CNPJ...'}
                            disabled={vincSalvando}
                            style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                          {vincDropOpen && vincQuery.trim().length >= 2 && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxWidth: 320, background: '#fff', border: '1px solid #d4dde8', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.12)', zIndex: 20, maxHeight: 200, overflowY: 'auto' }}>
                              {vincBuscando && <div style={{ padding: '8px 12px', fontSize: 12.5, color: '#94a3b8' }}>Buscando...</div>}
                              {!vincBuscando && vincResultados.length === 0 && (
                                <div style={{ padding: '8px 12px', fontSize: 12.5, color: '#94a3b8' }}>Nenhum cliente encontrado.</div>
                              )}
                              {vincResultados.map(c => (
                                <div key={c.id} onClick={() => vincularClienteTicket(c)}
                                  style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f3f7' }}
                                  onMouseDown={e => e.preventDefault()}>
                                  <div style={{ fontWeight: 600, color: '#1A2233' }}>{str(c.nome)}</div>
                                  {c.cnpj && <div style={{ fontSize: 11, color: '#94a3b8' }}>{str(c.cnpj)}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: tkt.descricao ? 14 : 0 }}>
                      {tkt.cliente_nome && (
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>SOLICITANTE</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <Avatar nome={tkt.cliente_nome} size={26} cor="#1A2233" />
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233' }}>{str(tkt.cliente_nome)}</span>
                          </div>
                        </div>
                      )}
                      {tkt.cliente_email && (
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>E-MAIL</div>
                          <div style={{ fontSize: 13, color: '#374151' }}>{str(tkt.cliente_email)}</div>
                        </div>
                      )}
                      {tkt.cliente_telefone && (
                        <div>
                          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>TELEFONE</div>
                          <div style={{ fontSize: 13, color: '#374151' }}>{str(tkt.cliente_telefone)}</div>
                        </div>
                      )}
                      {!tkt.cliente_nome && !tkt.cliente_email && !tkt.cliente_telefone && (
                        <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Nenhum dado de contato informado.</div>
                      )}
                    </div>
                    {tkt.descricao && (
                      <>
                        <div style={{ borderTop: '1px solid #e8ecf0', margin: '12px 0 10px' }} />
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 6 }}>DESCRICAO DA SOLICITACAO</div>
                        <div style={{ fontSize: 13.5, color: '#1A2233', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{str(tkt.descricao)}</div>
                      </>
                    )}
                  </div>
                )}

                {abaInferior === 'timeline' && !carregando && msgs.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>Nenhuma movimentacao registrada ainda.</div>
                )}

                {abaInferior === 'timeline' && msgs.map((m, i) => {
                  if (m.origem === 'sistema') return (
                    <div key={i} style={{ textAlign: 'center', margin: '8px 0 14px' }}>
                      <span style={{ display: 'inline-block', background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>{str(m.conteudo)}</span>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{fmtDt(m.criado_em)}</div>
                    </div>
                  );
                  const isInt = m.origem === 'interno', isAt = m.origem === 'atendente', isPub = m.origem === 'publico';
                  const isHistorico = m.tipo === 'chat_historico';
                  const urlImg = urlImagemMsg(m);
                  return (
                    <div key={i} className="mitem" style={isHistorico ? { opacity: .82 } : undefined}>
                      <Avatar nome={m.usuario_nome || 'S'} size={32} cor={isHistorico ? '#25D366' : isInt ? '#f39c12' : isAt ? '#D85A30' : '#1A2233'} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1A2233', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>{m.usuario_nome || 'Sistema'}</span>
                          {isHistorico && <span className="bdg" style={{ background: '#dcfce7', color: '#166534' }}>💬 HISTORICO DO CHAT</span>}
                          {!isHistorico && isInt && <span className="bdg" style={{ background: '#fef3c7', color: '#92400e' }}>NOTA INTERNA</span>}
                          {!isHistorico && isAt  && <span className="bdg" style={{ background: '#fee2e2', color: '#991b1b' }}>ATENDENTE</span>}
                          {!isHistorico && isPub && <span className="bdg" style={{ background: '#dbeafe', color: '#1e40af' }}>PUBLICO</span>}
                        </div>
                        {urlImg ? (
                          <img src={urlImg} alt="Anexo" onClick={() => setFotoAmpliada({ url: urlImg, index: fotosDoTicket.indexOf(m) })}
                            style={{ maxWidth: 220, maxHeight: 220, borderRadius: 10, border: '1px solid #e2e8f0', cursor: 'pointer', display: 'block' }} />
                        ) : (
                          <div className={`mbub ${isHistorico ? 'mhist' : isInt ? 'mint' : isAt ? 'mat' : 'mpub'}`}>{str(m.conteudo)}</div>
                        )}
                        <div className="mmeta">{fmtDt(m.criado_em)}{isHistorico && ' · copiado do chat WhatsApp'}</div>
                      </div>
                    </div>
                  );
                })}

                {abaInferior === 'fotos' && (
                  fotosDoTicket.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: 13 }}>Nenhuma foto anexada a este ticket ainda.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10 }}>
                      {fotosDoTicket.map((m, i) => (
                        <img key={i} src={urlImagemMsg(m)} alt="Anexo" onClick={() => setFotoAmpliada({ url: urlImagemMsg(m), index: i })}
                          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }} />
                      ))}
                    </div>
                  )
                )}
              </div>

              <div className="tinp">
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                  {['interno', 'publico', 'atendente'].map(t => (
                    <button key={t} className={`tabt ${abaInferior === 'timeline' && tipoMsg === t ? 'on' : ''}`}
                      onClick={() => { setTipoMsg(t); setAbaInferior('timeline'); }}>
                      {t === 'interno' ? 'Nota interna' : t === 'publico' ? 'Publico' : 'Atendente'}
                    </button>
                  ))}
                  <button className={`tabt ${abaInferior === 'fotos' ? 'on' : ''}`} onClick={() => setAbaInferior('fotos')}
                    style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
                    📷 Fotos{fotosDoTicket.length > 0 && (
                      <span style={{ background: abaInferior === 'fotos' ? 'rgba(255,255,255,.25)' : '#fae2da', color: abaInferior === 'fotos' ? '#fff' : '#993c1d', fontSize: 10.5, fontWeight: 700, borderRadius: 10, padding: '1px 6px' }}>
                        {fotosDoTicket.length}
                      </span>
                    )}
                  </button>
                </div>

                {anexoPendente && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
                      <img src={anexoPendente.previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid #d4dde8' }} />
                      <button onClick={removerAnexoPendente} title="Remover"
                        style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#fff', border: '1px solid #d4dde8', cursor: 'pointer', fontSize: 10, lineHeight: '16px', padding: 0 }}>✕</button>
                    </div>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}>Foto anexada — será enviada com a movimentação</span>
                  </div>
                )}
                {erroAnexo && <div style={{ fontSize: 11.5, color: '#dc2626', marginBottom: 6 }}>{erroAnexo}</div>}

                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <textarea value={novaMsg} onChange={e => setNovaMsg(e.target.value)}
                      placeholder={tipoMsg === 'interno' ? 'Nota interna (visivel apenas para a equipe)...' : 'Descrever movimentacao...'}
                      rows={3} onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) enviarMovimentacao(); }}
                      style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '9px 34px 9px 12px', fontSize: 13.5, resize: 'none', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' }} />
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={selecionarFoto} style={{ display: 'none' }} />
                    <button onClick={() => fileInputRef.current?.click()} title="Anexar foto"
                      style={{ position: 'absolute', right: 6, bottom: 6, width: 26, height: 26, borderRadius: 6, border: '1px solid #d4dde8', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📷</button>
                  </div>
                  <button onClick={enviarMovimentacao} disabled={enviando || enviandoAnexo || (!novaMsg.trim() && !anexoPendente)}
                    style={{ background: '#D85A30', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', opacity: (!novaMsg.trim() && !anexoPendente) ? .5 : 1, height: 44, flexShrink: 0 }}>
                    {enviando || enviandoAnexo ? '...' : 'Registrar'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Ctrl+Enter para enviar</div>
              </div>
            </div>

            {fotoAmpliada && (
              <div onClick={() => setFotoAmpliada(null)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(10,14,26,.85)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <button onClick={() => setFotoAmpliada(null)}
                  style={{ position: 'absolute', top: 16, right: 20, background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', borderRadius: 8, width: 34, height: 34, fontSize: 16, cursor: 'pointer' }}>✕</button>
                <img src={fotoAmpliada.url} alt="Foto ampliada" onClick={e => e.stopPropagation()}
                  style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8, boxShadow: '0 24px 80px rgba(0,0,0,.4)' }} />
              </div>
            )}

            {/* DIREITA */}
            <div className="tright">
              {tkt.empresa_nome && (
                <div className="cr">
                  <label>Cliente</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233' }}>&#127970; {str(tkt.empresa_nome)}</span>
                    <span title="Identificado pelo CNPJ no WhatsApp" style={{ fontSize: 9.5, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap' }}>
                      🔗 Vinculado
                    </span>
                  </div>
                </div>
              )}
              <div className="cr">
                <label>{labelsCustom.cliente_nome || 'Solicitante'}</label>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Avatar nome={tkt.cliente_nome} size={30} cor="#1A2233" />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233' }}>{str(tkt.cliente_nome) || '—'}</div>
                    {tkt.cliente_email    && <div style={{ fontSize: 12, color: '#6b7a8d', marginTop: 2 }}>{str(tkt.cliente_email)}</div>}
                    {tkt.cliente_telefone && <div style={{ fontSize: 12, color: '#6b7a8d' }}>{str(tkt.cliente_telefone)}</div>}
                  </div>
                </div>
              </div>

              <div className="cr"><label>Status</label>
                <select value={tkt.status} onChange={e => salvarCampo('status', e.target.value)}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <div className="cr"><label>{labelsCustom.prioridade || 'Prioridade'}</label>
                <select value={tkt.prioridade} onChange={e => salvarCampo('prioridade', e.target.value)}>
                  {Object.entries(PRIORIDADE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <SlaBar prazo={tkt.sla_prazo} />
              </div>

              {/* Campos customizados (Ordem de Serviço) — só aparece se a empresa tiver algum cadastrado */}
              {camposCustomDefs.map(c => {
                const valor = (tkt.campos_customizados || {})[c.nome_interno] ?? '';
                return (
                  <div className="cr" key={c.id}>
                    <label>{c.label}{c.obrigatorio ? ' *' : ''}</label>
                    {c.tipo === 'select' ? (
                      <select value={valor} onChange={e => salvarCampoCustomizado(c.nome_interno, e.target.value)}>
                        <option value="">Selecione...</option>
                        {(c.opcoes || []).map(op => <option key={op} value={op}>{op}</option>)}
                      </select>
                    ) : c.tipo === 'textarea' ? (
                      <textarea defaultValue={valor} onBlur={e => salvarCampoCustomizado(c.nome_interno, e.target.value)}
                        style={{ width: '100%', minHeight: 60, fontFamily: 'inherit' }} />
                    ) : (
                      <input type={c.tipo === 'numero' ? 'number' : c.tipo === 'data' ? 'date' : 'text'}
                        defaultValue={valor} onBlur={e => salvarCampoCustomizado(c.nome_interno, e.target.value)} />
                    )}
                  </div>
                );
              })}

              <div className="cr"><label>Tipo de Ticket</label>
                <select value={tkt.tipo_id || ''} onChange={e => salvarCampo('tipo_id', e.target.value || null)}>
                  <option value="">— Selecionar —</option>
                  {tipos.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>

              <div className="cr"><label>Categoria</label>
                <select value={tkt.categoria_id || ''} onChange={e => {
                  const v = e.target.value || null;
                  salvarCampo('categoria_id', v); salvarCampo('subcategoria_id', null);
                  setSubcats(categorias.find(c => c.id == v)?.subcategorias || []);
                }}>
                  <option value="">— Selecionar —</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>

              {subcats.length > 0 && (
                <div className="cr"><label>Subcategoria</label>
                  <select value={tkt.subcategoria_id || ''} onChange={e => salvarCampo('subcategoria_id', e.target.value || null)}>
                    <option value="">— Selecionar —</option>
                    {subcats.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              <div className="cr"><label>Mesa de Trabalho</label>
                <select value={tkt.mesa_id || ''} onChange={e => { salvarCampo('mesa_id', e.target.value || null); salvarCampo('atendente_id', null); }}>
                  <option value="">— Selecionar —</option>
                  {mesas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </select>
              </div>

              <div className="cr"><label>Operador</label>
                <select value={tkt.atendente_id || ''} onChange={e => salvarCampo('atendente_id', e.target.value || null)}>
                  <option value="">— Selecionar —</option>
                  {(mesaAtual ? usuarios.filter(u => (mesaAtual.membros_ids || []).includes(u.id)) : usuarios).map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
                {operAtual && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <Avatar nome={operAtual.nome} size={22} cor={operAtual.cor || '#1A2233'} />
                    <span style={{ fontSize: 12, color: '#1A2233' }}>{operAtual.nome}</span>
                  </div>
                )}
              </div>

              <div className="cr"><label>Tempo Total</label>
                <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: '#1A2233', letterSpacing: 1 }}>{fmtTempo(tkt.tempo_total_segundos || 0)}</div>
                {tkt.tipo_atendimento && <div style={{ fontSize: 12, color: '#6b7a8d', marginTop: 3 }}>{str(tkt.tipo_atendimento) === 'interno' ? 'Interno' : 'Externo'}</div>}
                {sessoes.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>Sessoes</div>
                    {sessoes.map((s, i) => (
                      <div key={i} className="srow">
                        <span>{fmtDt(s.inicio)}</span>
                        <span style={{ fontFamily: 'monospace' }}>{s.duracao_segundos ? fmtTempo(s.duracao_segundos) : 'em aberto'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="cr"><label>Datas</label>
                {[['Criacao', tkt.criado_em], ['SLA', tkt.sla_prazo], ['Chegada', tkt.data_chegada], ['Saida', tkt.data_saida]].map(([l, v]) => (
                  <div key={l} className="drow"><span style={{ color: '#94a3b8' }}>{l}</span><span style={{ color: '#1A2233', fontWeight: 500 }}>{fmtDt(v)}</span></div>
                ))}
              </div>

              <div className="cr"><label>Detalhes</label>
                {[['Fila', str(tkt.fila)], ['Origem', str(tkt.origem)], ['Categoria', str(catAtual?.nome)], ['Subcategoria', str(subcatAtual?.nome)], ['Mesa', str(mesaAtual?.nome)]]
                  .filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} className="drow"><span style={{ color: '#94a3b8' }}>{l}</span><span style={{ color: '#1A2233', fontWeight: 500 }}>{v}</span></div>
                  ))}
              </div>

              {tkt.comentario_finalizacao && (
                <div className="cr"><label>Resolucao</label>
                  <div style={{ fontSize: 13, color: '#374151', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: 9, lineHeight: 1.55 }}>{str(tkt.comentario_finalizacao)}</div>
                </div>
              )}

              {visualizadores.length > 0 && (
                <div className="cr"><label>Quem viu este ticket</label>
                  {visualizadores.map((v, i) => (
                    <div key={i} className="vrow">
                      <Avatar nome={v.nome} size={22} cor="#6b7a8d" />
                      <span style={{ flex: 1 }}>{v.nome}</span>
                      <span style={{ color: '#94a3b8', fontSize: 10 }}>{fmtDt(v.visto_em)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CHECKLIST OBRIGATÓRIO */}
      {checklistModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1A2233', marginBottom: 6 }}>✅ Checklist Obrigatório</div>
            <p style={{ color: '#6b7a8d', fontSize: 13.5, marginBottom: 22, lineHeight: 1.6 }}>
              Responda todos os itens abaixo para finalizar o ticket <strong>#{tkt.numero || tkt.id}</strong>.
              Todos os campos são obrigatórios.
            </p>

            {checklistModal.checklists.map(cl => (
              <div key={cl.id} style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1A2233', marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid #D85A30' }}>{cl.nome}</div>
                {(cl.itens || []).map(item => {
                  const tipo  = item.tipo || 'checkbox';
                  const resp  = checklistModal.respostas[cl.id]?.[item.id];
                  const opcoes = item.opcoes
                    ? (typeof item.opcoes === 'string' ? JSON.parse(item.opcoes) : item.opcoes)
                    : [];
                  const setResp = (val) => setChecklistModal(p => ({
                    ...p,
                    respostas: { ...p.respostas, [cl.id]: { ...p.respostas[cl.id], [item.id]: val } },
                  }));

                  /* ── CHECKBOX ── */
                  if (tipo === 'checkbox') {
                    return (
                      <label key={item.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px',
                        borderRadius: 8, marginBottom: 8, cursor: 'pointer',
                        background: resp ? '#f0fdf4' : '#fafafa',
                        border: `1.5px solid ${resp ? '#86efac' : '#d4dde8'}`, transition: 'all .15s',
                      }}>
                        <input type="checkbox" style={{ marginTop: 2, width: 16, height: 16, accentColor: '#D85A30', flexShrink: 0 }}
                          checked={!!resp} onChange={e => setResp(e.target.checked)} />
                        <span style={{ fontSize: 13.5, lineHeight: 1.5, color: resp ? '#166534' : '#374151', fontWeight: resp ? 600 : 400 }}>{item.descricao}</span>
                      </label>
                    );
                  }

                  /* ── MÚLTIPLA ESCOLHA ── */
                  if (tipo === 'multipla') {
                    return (
                      <div key={item.id} style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: '#fafafa', border: `1.5px solid ${resp ? '#86efac' : '#d4dde8'}` }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233', marginBottom: 10 }}>{item.descricao}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {opcoes.map((op, oi) => {
                            const sel = resp === op;
                            return (
                              <label key={oi} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                borderRadius: 7, cursor: 'pointer',
                                background: sel ? '#fff3eb' : 'white',
                                border: `1.5px solid ${sel ? '#D85A30' : '#e2e8f0'}`,
                              }}>
                                <div style={{
                                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                                  border: `2px solid ${sel ? '#D85A30' : '#d4dde8'}`,
                                  background: sel ? '#D85A30' : 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  {sel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
                                </div>
                                <input type="radio" name={`item-${item.id}`} value={op} checked={sel}
                                  onChange={() => setResp(op)} style={{ display: 'none' }} />
                                <span style={{ fontSize: 13, color: sel ? '#D85A30' : '#374151', fontWeight: sel ? 600 : 400 }}>{op}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  /* ── DISSERTATIVA ── */
                  return (
                    <div key={item.id} style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: '#fafafa', border: `1.5px solid ${resp?.trim() ? '#86efac' : '#d4dde8'}` }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1A2233', marginBottom: 8 }}>{item.descricao}</div>
                      <textarea
                        value={resp || ''}
                        onChange={e => setResp(e.target.value)}
                        placeholder="Digite sua resposta aqui..."
                        rows={3}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid #d4dde8', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                  );
                })}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={confirmarChecklist}
                style={{ flex: 1, background: '#D85A30', color: '#fff', border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Confirmar e Continuar
              </button>
              <button onClick={() => setChecklistModal(null)}
                style={{ background: '#f0f2f5', border: 'none', borderRadius: 9, padding: '11px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer', color: '#6b7a8d' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PAUSAR */}
      {modalPausar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 40px rgba(0,0,0,.22)' }}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Pausar atendimento</div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d' }}>Motivo da pausa (opcional)</label>
            <textarea value={motivoPausa} onChange={e => setMotivoPausa(e.target.value)} rows={3}
              style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 10px', marginTop: 6, fontSize: 13.5, resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalPausar(false)} style={{ background: '#f0f2f5', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={pausar} style={{ background: '#f39c12', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer' }}>Pausar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FINALIZAR */}
      {modalFinalizar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 460, boxShadow: '0 8px 40px rgba(0,0,0,.22)' }}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Finalizar ticket</div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d' }}>Comentario / Resolucao</label>
            <textarea value={comentFinal} onChange={e => setComentFinal(e.target.value)} rows={4} placeholder="Descreva o que foi feito..."
              style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 10px', marginTop: 6, marginBottom: 16, fontSize: 13.5, resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 14, marginBottom: 20, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d', display: 'block', marginBottom: 6 }}>Tipo de atendimento</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['interno', 'externo'].map(t => (
                    <button key={t} onClick={() => setTipoAtend(t)} style={{ flex: 1, padding: '8px 0', borderRadius: 7, border: `2px solid ${tipoAtend === t ? '#1A2233' : '#d4dde8'}`, background: tipoAtend === t ? '#1A2233' : '#fff', color: tipoAtend === t ? '#fff' : '#1A2233', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      {t === 'interno' ? 'Interno' : 'Externo'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d', display: 'block', marginBottom: 6 }}>Tempo (HH:MM)</label>
                <input type="text" value={tempoFinal} onChange={e => setTempoFinal(e.target.value)}
                  style={{ border: '1.5px solid #d4dde8', borderRadius: 8, padding: '9px 12px', fontSize: 16, fontFamily: 'monospace', fontWeight: 700, width: 96, outline: 'none', textAlign: 'center' }} />
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, textAlign: 'center' }}>editavel se necessario</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d', display: 'block', marginBottom: 6 }}>Data de abertura</label>
                <input type="datetime-local" value={dataAbertura} onChange={e => setDataAbertura(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>editavel se necessario</div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d', display: 'block', marginBottom: 6 }}>Data de finalização</label>
                <input type="datetime-local" value={dataFinalizacao} onChange={e => setDataFinalizacao(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>editavel se necessario</div>
              </div>
            </div>
            {assinaturaAtiva && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
                <AssinaturaCanvas label="Assinatura do Técnico" largura={396} altura={90} onChange={setAssinaturaTecnico} />
                <AssinaturaCanvas label="Assinatura do Cliente" largura={396} altura={90} onChange={setAssinaturaCliente} />
              </div>
            )}
            {erroFinalizar && (
              <div style={{ background: '#fde8e8', color: '#c0392b', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                ⚠ {erroFinalizar}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalFinalizar(false)} style={{ background: '#f0f2f5', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={finalizar} style={{ background: '#D85A30', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' }}>Finalizar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TRANSFERIR */}
      {modalTransferir && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 8px 40px rgba(0,0,0,.22)' }}>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>Transferir ticket</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {['tecnico', 'fila'].map(m => (
                <button key={m} onClick={() => setTransMode(m)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${transMode === m ? '#1A2233' : '#d4dde8'}`, background: transMode === m ? '#1A2233' : '#fff', color: transMode === m ? '#fff' : '#1A2233', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {m === 'tecnico' ? 'Para tecnico' : 'Para fila'}
                </button>
              ))}
            </div>
            {transMode === 'tecnico' ? (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d' }}>Tecnico</label>
                <select value={transTecnico} onChange={e => setTransTecnico(e.target.value)} style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '9px 10px', fontSize: 13.5, marginTop: 6, outline: 'none' }}>
                  <option value="">Selecionar</option>
                  {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              </>
            ) : (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7a8d' }}>Fila de destino</label>
                <input value={transFila} onChange={e => setTransFila(e.target.value)} placeholder="Nome da fila" style={{ width: '100%', border: '1.5px solid #d4dde8', borderRadius: 8, padding: '9px 10px', fontSize: 13.5, marginTop: 6, outline: 'none', boxSizing: 'border-box' }} />
              </>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalTransferir(false)} style={{ background: '#f0f2f5', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={transferir} style={{ background: '#1A2233', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' }}>Transferir</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════ KANBAN CARD ═══════ */
function KanbanCard({ tkt, onClick, usuarios, isGestor, onDelete }) {
  const prio = PRIORIDADE[tkt.prioridade] || PRIORIDADE.media;
  const oper = usuarios.find(u => u.id === tkt.atendente_id);
  const est  = tkt.sla_prazo && new Date(tkt.sla_prazo) < new Date();

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Apagar o ticket #${tkt.numero || tkt.id} — "${tkt.titulo}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api(`/tickets/${tkt.id}`, { method: 'DELETE' });
      onDelete?.();
    } catch {
      alert('Erro ao apagar ticket.');
    }
  };

  return (
    <div onClick={onClick} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 10, border: `1px solid ${est ? '#fca5a5' : '#e8ecf0'}`, boxShadow: est ? '0 0 0 2px #fca5a5' : '0 1px 4px rgba(0,0,0,.06)', cursor: 'pointer', transition: 'box-shadow .15s, transform .1s', position: 'relative' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = est ? '0 0 0 2px #fca5a5' : '0 1px 4px rgba(0,0,0,.06)'; e.currentTarget.style.transform = ''; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>#{tkt.numero || tkt.id}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: prio.cor, background: prio.cor + '18', padding: '2px 7px', borderRadius: 10 }}>{prio.label.toUpperCase()}</span>
          {isGestor && (
            <button
              onClick={handleDelete}
              title="Apagar ticket"
              style={{ background: '#fde8e8', border: 'none', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 12, color: '#e74c3c', fontWeight: 700, lineHeight: 1.4, flexShrink: 0 }}>
              🗑
            </button>
          )}
        </div>
      </div>
      <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1A2233', marginBottom: 6, lineHeight: 1.35 }}>{str(tkt.titulo)}</div>
      {tkt.empresa_nome && (
        <div style={{ fontSize: 12, color: '#0369a1', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span>&#127970; {str(tkt.empresa_nome)}</span>
          <span title="Identificado pelo CNPJ no WhatsApp" style={{ fontSize: 9.5, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap' }}>
            🔗
          </span>
        </div>
      )}
      {tkt.cliente_nome && (
        <div style={{ fontSize: 12, color: '#6b7a8d', marginBottom: 4 }}>&#128100; {str(tkt.cliente_nome)}</div>
      )}
      {tkt.fila         && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>&#128194; {str(tkt.fila)}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {oper ? <Avatar nome={oper.nome} size={24} cor={oper.cor || '#1A2233'} /> : <span />}
        {est  && <span style={{ fontSize: 11, color: '#e74c3c', fontWeight: 700 }}>SLA</span>}
      </div>
      <SlaBar prazo={tkt.sla_prazo} />
    </div>
  );
}


/* ═══════ MODAL NOVO TICKET ═══════ */
function ModalNovoTicket({ tipos, categorias, mesas, usuarios, onClose, onCriado }) {
  const [form, setForm] = useState({
    titulo: '', descricao: '', cliente_id: '', cliente_nome: '', cliente_telefone: '',
    cliente_email: '', prioridade: 'media', fila: 'suporte',
    tipo_id: '', categoria_id: '', subcategoria_id: '', mesa_id: '',
    atendente_id: '', sla_horas: 24,
  });
  const [subcats, setSubcats]         = useState([]);
  const [salvando, setSalvando]       = useState(false);
  const [erro, setErro]               = useState('');
  const [membrosMesa, setMembrosMesa] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── Busca de cliente cadastrado (nome ou CNPJ) ───────────────────────────────
  const [clienteQuery, setClienteQuery]         = useState('');
  const [clienteResultados, setClienteResultados] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [buscandoCliente, setBuscandoCliente]    = useState(false);
  const [clienteDropOpen, setClienteDropOpen]    = useState(false);
  const clienteDebounce = useRef(null);

  function buscarClientes(q) {
    setClienteQuery(q);
    setClienteDropOpen(true);
    if (clienteDebounce.current) clearTimeout(clienteDebounce.current);
    if (q.trim().length < 2) { setClienteResultados([]); return; }
    clienteDebounce.current = setTimeout(async () => {
      setBuscandoCliente(true);
      try {
        const r = await api(`/tickets/clientes/buscar?q=${encodeURIComponent(q.trim())}`);
        setClienteResultados(Array.isArray(r) ? r : []);
      } catch { setClienteResultados([]); }
      setBuscandoCliente(false);
    }, 350);
  }

  function selecionarCliente(cli) {
    setClienteSelecionado(cli);
    set('cliente_id', cli.id);
    setClienteQuery(cli.nome);
    setClienteDropOpen(false);
    setClienteResultados([]);
  }

  function limparCliente() {
    setClienteSelecionado(null);
    set('cliente_id', '');
    setClienteQuery('');
  }

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  function onCategoria(id) {
    set('categoria_id', id); set('subcategoria_id', '');
    setSubcats(categorias.find(c => c.id == id)?.subcategorias || []);
  }

  function onMesa(id) {
    set('mesa_id', id); set('atendente_id', '');
    const m = mesas.find(x => x.id == id);
    setMembrosMesa(m ? usuarios.filter(u => (m.membros_ids || []).includes(u.id)) : []);
  }

  async function salvar() {
    const faltando = [];
    if (!form.titulo.trim())          faltando.push('Assunto');
    if (!form.descricao.trim())       faltando.push('Descrição');
    if (!form.cliente_id)             faltando.push('Cliente');
    if (!form.cliente_nome.trim())    faltando.push('Solicitante');
    if (!form.cliente_telefone.trim()) faltando.push('Telefone');
    if (!form.tipo_id)                faltando.push('Tipo de Ticket');
    if (!form.categoria_id)           faltando.push('Categoria');
    if (!form.mesa_id)                faltando.push('Mesa de Trabalho');
    if (faltando.length) {
      setErro(`Campos obrigatórios não preenchidos: ${faltando.join(', ')}.`);
      return;
    }
    setErro('');
    setSalvando(true);
    try {
      const body = { ...form };
      ['subcategoria_id', 'atendente_id'].forEach(k => {
        if (!body[k]) body[k] = null;
      });
      const r = await api('/tickets', { method: 'POST', body: JSON.stringify(body) });
      if (r?.erro) { setErro(r.erro); setSalvando(false); return; }
      onCriado(); onClose();
    } catch { setErro('Erro ao criar ticket. Tente novamente.'); }
    setSalvando(false);
  }

  const inp = { border: '1px solid #d4dde8', borderRadius: 7, padding: '7px 10px', fontSize: 13, background: '#fff', color: '#1A2233', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl = { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: 6 };
  const cr  = { padding: '12px 16px', borderBottom: '1px solid #e8ecf0' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,14,26,.76)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 860, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,.3)' }}>
        <div style={{ background: '#1A2233', color: '#fff', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
          <span style={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Novo Ticket</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1, opacity: .7 }}>×</button>
        </div>
        <div style={{ display: 'flex', flex: 1, overflow: isMobile ? 'auto' : 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1, overflowY: isMobile ? 'visible' : 'auto', borderRight: isMobile ? 'none' : '1px solid #e8ecf0', borderBottom: isMobile ? '1px solid #e8ecf0' : 'none' }}>
            <div style={cr}>
              <label style={lbl}>Assunto *</label>
              <input value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Assunto do ticket" style={inp} />
            </div>
            <div style={{ ...cr, position: 'relative' }}>
              <label style={lbl}>Cliente *</label>
              {clienteSelecionado ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #d4dde8', borderRadius: 7, padding: '7px 10px', background: '#f0f9ff' }}>
                  <span style={{ fontSize: 13, color: '#0369a1', fontWeight: 600, flex: 1 }}>&#127970; {str(clienteSelecionado.nome)}</span>
                  <button onClick={limparCliente} style={{ fontSize: 11, color: '#e74c3c', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 700 }}>× trocar</button>
                </div>
              ) : (
                <>
                  <input value={clienteQuery}
                    onChange={e => buscarClientes(e.target.value)}
                    onFocus={() => setClienteDropOpen(true)}
                    onBlur={() => setTimeout(() => setClienteDropOpen(false), 150)}
                    placeholder="Buscar por nome ou CNPJ..." style={inp} />
                  {clienteDropOpen && (clienteQuery.trim().length >= 2) && (
                    <div style={{ position: 'absolute', top: '100%', left: 16, right: 16, background: '#fff', border: '1px solid #d4dde8', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.12)', zIndex: 20, maxHeight: 220, overflowY: 'auto' }}>
                      {buscandoCliente && <div style={{ padding: '8px 12px', fontSize: 12.5, color: '#94a3b8' }}>Buscando...</div>}
                      {!buscandoCliente && clienteResultados.length === 0 && (
                        <div style={{ padding: '8px 12px', fontSize: 12.5, color: '#94a3b8' }}>Nenhum cliente encontrado.</div>
                      )}
                      {clienteResultados.map(c => (
                        <div key={c.id} onClick={() => selecionarCliente(c)}
                          style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f3f7' }}
                          onMouseDown={e => e.preventDefault()}>
                          <div style={{ fontWeight: 600, color: '#1A2233' }}>{str(c.nome)}</div>
                          {c.cnpj && <div style={{ fontSize: 11, color: '#94a3b8' }}>{str(c.cnpj)}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={cr}>
              <label style={lbl}>Solicitante *</label>
              <input value={form.cliente_nome} onChange={e => set('cliente_nome', e.target.value)}
                placeholder="Nome de quem está abrindo o chamado" style={inp} />
            </div>
            <div style={cr}>
              <label style={lbl}>Telefone *</label>
              <input value={form.cliente_telefone} onChange={e => set('cliente_telefone', e.target.value)} placeholder="(00) 00000-0000" style={inp} />
            </div>
            <div style={cr}>
              <label style={lbl}>E-mail</label>
              <input type="email" value={form.cliente_email} onChange={e => set('cliente_email', e.target.value)}
                placeholder="email@exemplo.com" style={inp} />
            </div>
            <div style={cr}>
              <label style={lbl}>Descrição da solicitação *</label>
              <textarea value={form.descricao} onChange={e => set('descricao', e.target.value)} rows={6}
                placeholder="Descreva o problema ou solicitação..." style={{ ...inp, resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ width: isMobile ? '100%' : 300, overflowY: isMobile ? 'visible' : 'auto', background: '#f8fafc', flexShrink: 0, boxSizing: 'border-box' }}>
            <div style={cr}>
              <label style={lbl}>Tipo de Ticket *</label>
              <select value={form.tipo_id} onChange={e => set('tipo_id', e.target.value)} style={inp}>
                <option value="">— Selecionar —</option>
                {tipos.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
            <div style={cr}>
              <label style={lbl}>Categoria *</label>
              <select value={form.categoria_id} onChange={e => onCategoria(e.target.value)} style={inp}>
                <option value="">— Selecionar —</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            {subcats.length > 0 && (
              <div style={cr}>
                <label style={lbl}>Subcategoria</label>
                <select value={form.subcategoria_id} onChange={e => set('subcategoria_id', e.target.value)} style={inp}>
                  <option value="">— Selecionar —</option>
                  {subcats.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}
            <div style={cr}>
              <label style={lbl}>Mesa de Trabalho *</label>
              <select value={form.mesa_id} onChange={e => onMesa(e.target.value)} style={inp}>
                <option value="">— Selecionar —</option>
                {mesas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </div>
            <div style={cr}>
              <label style={lbl}>Operador</label>
              <select value={form.atendente_id} onChange={e => set('atendente_id', e.target.value)} style={inp}>
                <option value="">— Selecionar —</option>
                {(membrosMesa.length > 0 ? membrosMesa : usuarios).map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div style={cr}>
              <label style={lbl}>Prioridade</label>
              <select value={form.prioridade} onChange={e => set('prioridade', e.target.value)} style={inp}>
                {Object.entries(PRIORIDADE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={cr}>
              <label style={lbl}>SLA (horas)</label>
              <input type="number" value={form.sla_horas} onChange={e => set('sla_horas', e.target.value)} min={1} max={720} style={{ ...inp, width: 110 }} />
            </div>
          </div>
        </div>
        {erro && (
          <div style={{ background: '#fde8e8', color: '#c0392b', padding: '8px 18px', fontSize: 13, fontWeight: 600 }}>⚠ {erro}</div>
        )}
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e8ecf0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fff' }}>
          <button onClick={onClose} style={{ background: '#f0f2f5', border: 'none', borderRadius: 8, padding: '9px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{ background: '#D85A30', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: salvando ? .7 : 1 }}>
            {salvando ? 'Salvando...' : '+ Criar Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════ KANBAN COLUNA (com Ver mais) ═══════ */
function KanbanColuna({ col, tickets, usuarios, isGestor, onOpen, onDelete, isMobile }) {
  const [expandida, setExpandida] = useState(false);
  const visiveis = expandida ? tickets : tickets.slice(0, 3);
  return (
    <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 12px', border: '1px solid #e8ecf0', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: `2.5px solid ${col.cor}` }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#1A2233' }}>{col.label}</span>
        <span style={{ background: col.cor, color: '#fff', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>{tickets.length}</span>
      </div>
      {tickets.length === 0 && (
        <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 12, padding: '20px 0' }}>Sem tickets</div>
      )}
      {visiveis.map(t => (
        <KanbanCard key={t.id} tkt={t} usuarios={usuarios} isGestor={isGestor}
          onClick={() => onOpen(t)} onDelete={onDelete} />
      ))}
      {tickets.length > 3 && (
        <button onClick={() => setExpandida(e => !e)}
          style={{ width: '100%', marginTop: 6, padding: '7px 0', background: 'none', border: '1.5px dashed #d4dde8', borderRadius: 8, color: '#6b7a8d', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {expandida ? '▲ Ver menos' : `▼ Ver mais ${tickets.length - 3} ticket${tickets.length - 3 > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
}

/* ═══════ VISÃO FILTRADA (grade de cards + mini gráficos) ═══════
   Substitui o Kanban quando um quadrado de KPI está ativo. Reaproveita o
   KanbanCard (mesmo visual dos cards do Kanban) e o TicketPopup ao clicar
   (via onOpen), então nada muda no fluxo de abrir/editar um ticket. */
function VisaoFiltrada({ filtroInfo, lista, usuarios, isGestor, onOpen, onDelete, onLimpar, isMobile }) {
  const stats = estatisticasFiltro(lista);
  return (
    <div>
      {/* Barra do filtro ativo */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
        background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '12px 16px', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: filtroInfo.cor, flexShrink: 0 }} />
          <span style={{ color: '#6b7a8d' }}>Filtro ativo:</span>
          <span style={{ fontWeight: 700, color: '#1A2233' }}>{filtroInfo.label}</span>
          <span style={{ color: '#94a3b8' }}>({lista.length} ticket{lista.length === 1 ? '' : 's'})</span>
        </div>
        <button onClick={onLimpar} style={{
          display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #d4dde8',
          borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#1A2233', cursor: 'pointer',
        }}>
          × Limpar filtro · Voltar ao Kanban
        </button>
      </div>

      {/* Mini gráficos — calculados a partir dos tickets filtrados (dados reais) */}
      {lista.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1.2fr 1fr', gap: 16, marginBottom: 16,
        }}>
          <div style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A2233', marginBottom: 12 }}>Tickets por fila</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <DonutMini dados={stats.porFila} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {stats.porFila.map(d => (
                  <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7a8d' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.cor, flexShrink: 0 }} />
                    {d.label} · {d.count}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A2233', marginBottom: 12 }}>Tickets por prioridade</div>
            <BarrasHorizontais dados={stats.porPrioridade} />
          </div>

          <div style={{ background: '#fff', border: '1px solid #e8ecf0', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,.06)', display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A2233' }}>Tempo médio em atraso</div>
            {stats.atrasoMedioTexto ? (
              <>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1A2233' }}>{stats.atrasoMedioTexto}</div>
                <div style={{ fontSize: 12, color: '#e74c3c', fontWeight: 600 }}>{stats.totalAtrasados} ticket{stats.totalAtrasados === 1 ? '' : 's'} com SLA vencido</div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>Nenhum ticket com SLA estourado neste filtro.</div>
            )}
          </div>
        </div>
      )}

      {/* Grade de cards — mesmo visual do Kanban, sem colunas */}
      {lista.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '40px 0', background: '#f8fafc', borderRadius: 10, border: '1px solid #e8ecf0' }}>
          Nenhum ticket encontrado para este filtro (considerando também a busca/prioridade/fila ativas acima).
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {lista.map(t => (
            <KanbanCard key={t.id} tkt={t} usuarios={usuarios} isGestor={isGestor} onClick={() => onOpen(t)} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════ PAGINA PRINCIPAL ═══════ */
export default function Tickets({ usuario: usuarioProp, toast }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  // Lê perfis da prop recebida do App.js (fonte mais confiável)
  // Fallback: tenta localStorage caso o componente seja usado sem a prop
  const perfis = (() => {
    if (usuarioProp?.perfis?.length > 0) return usuarioProp.perfis;
    if (usuarioProp?.perfil) return [usuarioProp.perfil];
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (u?.perfis?.length > 0) return u.perfis;
      if (u?.perfil) return [u.perfil];
    } catch {}
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.perfis?.length > 0) return payload.perfis;
        if (payload?.perfil) return [payload.perfil];
      }
    } catch {}
    return [];
  })();

  const isGestor = perfis.some(p => p.toLowerCase() === 'ges');
  const [tickets, setTickets]       = useState([]);
  const [dashboard, setDashboard]   = useState({});
  const [usuarios, setUsuarios]     = useState([]);
  const [tipos, setTipos]           = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mesas, setMesas]           = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca]           = useState('');
  const [filtroPrio, setFiltroPrio] = useState('');
  const [filtroFila, setFiltroFila] = useState('');
  const [tktAberto, setTktAberto]   = useState(null);
  const [modalNovo, setModalNovo]   = useState(false);
  const [filtroKpi, setFiltroKpi]   = useState(null); // kpi ativo (quadrado clicado) ou null = Kanban normal

  // ─── Alerta sonoro — preferência global (lida/escrita pelo App.js) ───────────
  const [somAtivo, setSomAtivo] = useState(() => {
    try { return localStorage.getItem('som_alertas_ativo') !== 'false'; }
    catch { return true; }
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [t, dash, us, ti, ca, me] = await Promise.all([api('/tickets?limit=500'), api('/tickets/dashboard'), api('/usuarios'), api('/tickets/tipos'), api('/tickets/categorias'), api('/tickets/mesas')]);
      setTickets(Array.isArray(t) ? t : t.tickets || []);
      setDashboard(dash || {});
      setUsuarios(Array.isArray(us) ? us : []);
      setTipos(Array.isArray(ti) ? ti : []);
      setCategorias(Array.isArray(ca) ? ca : []);
      setMesas(Array.isArray(me) ? me : []);
    } catch (_) {}
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ─── Busca remota por número — garante achar o ticket em QUALQUER status,
  // mesmo que esteja fora da janela dos 500 tickets carregados localmente ───
  useEffect(() => {
    const termo = busca.trim();
    // só dispara quando o termo é puramente numérico (número do ticket)
    if (!/^\d{2,}$/.test(termo)) return;
    const timer = setTimeout(async () => {
      try {
        const r = await api(`/tickets?busca=${encodeURIComponent(termo)}&limit=20`);
        const encontrados = Array.isArray(r) ? r : (r.tickets || []);
        if (!encontrados.length) return;
        setTickets(prev => {
          const existentes = new Set(prev.map(t => t.id));
          const novos = encontrados.filter(t => !existentes.has(t.id));
          return novos.length ? [...prev, ...novos] : prev;
        });
      } catch (_) {}
    }, 350);
    return () => clearTimeout(timer);
  }, [busca]);

  const filtrados = tickets.filter(t => {
    const buscaLower = busca.toLowerCase().trim();
    const numeroExibido = String(t.numero ?? t.id ?? '');
    if (busca
      && !t.titulo?.toLowerCase().includes(buscaLower)
      && !t.cliente_nome?.toLowerCase().includes(buscaLower)
      && !t.empresa_nome?.toLowerCase().includes(buscaLower)
      && !numeroExibido.includes(buscaLower)
    ) return false;
    if (filtroPrio && t.prioridade !== filtroPrio) return false;
    if (filtroFila && t.fila !== filtroFila) return false;
    return true;
  });

  const dashCards = FILTROS_KPI.map(f => ({ ...f, val: dashboard[f.kpi] || 0 }));

  // Tickets do filtro de KPI ativo, já combinados com busca/prioridade/fila
  // (os mesmos filtros do topo continuam valendo dentro da visão filtrada).
  const ticketsFiltradosKpi = filtroKpi ? filtrados.filter(t => ticketBateFiltroKpi(t, filtroKpi)) : [];
  const filtroKpiInfo = FILTROS_KPI.find(f => f.kpi === filtroKpi);

  function toggleFiltroKpi(kpi) {
    setFiltroKpi(atual => (atual === kpi ? null : kpi));
  }

  return (
    <div style={{ padding: isMobile ? '12px 10px' : '24px 28px', boxSizing: 'border-box', width: '100%', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', gap: isMobile ? 8 : 14, marginBottom: isMobile ? 14 : 24, flexWrap: 'wrap' }}>
        {dashCards.map(c => {
          const ativo = filtroKpi === c.kpi;
          return (
            <div key={c.kpi} role="button" tabIndex={0}
              onClick={() => toggleFiltroKpi(c.kpi)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFiltroKpi(c.kpi); } }}
              title={ativo ? 'Clique para voltar ao Kanban' : `Ver tickets: ${c.label}`}
              style={{
                flex: isMobile ? '1 1 80px' : '1 1 150px',
                background: ativo ? '#fff4ef' : '#fff',
                borderRadius: 10, padding: isMobile ? '10px 12px' : '16px 20px',
                border: ativo ? '2px solid #D85A30' : '1px solid #e8ecf0',
                boxShadow: ativo ? '0 0 0 3px rgba(216,90,48,.12)' : '0 1px 4px rgba(0,0,0,.06)',
                cursor: 'pointer', userSelect: 'none', transition: 'box-shadow .15s, border-color .15s',
              }}>
              <div style={{ fontSize: isMobile ? 10 : 12, color: ativo ? '#B84A24' : '#6b7a8d', fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
              <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: c.cor }}>{c.val}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar..." style={{ flex: 1, minWidth: isMobile ? 120 : 200, border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }} />
        {!isMobile && (
          <select value={filtroPrio} onChange={e => setFiltroPrio(e.target.value)} style={{ border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#fff', outline: 'none' }}>
            <option value="">Todas prioridades</option>
            {Object.entries(PRIORIDADE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
        {!isMobile && (
          <input value={filtroFila} onChange={e => setFiltroFila(e.target.value)} placeholder="Filtrar por fila..." style={{ border: '1.5px solid #d4dde8', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', width: 140 }} />
        )}
        {/* ── Toggle de som ── */}
        <button
          onClick={() => {
            const novo = !somAtivo;
            setSomAtivo(novo);
            try { localStorage.setItem('som_alertas_ativo', String(novo)); } catch {}
          }}
          title={somAtivo ? 'Silenciar alertas sonoros' : 'Ativar alertas sonoros'}
          style={{
            background: somAtivo ? '#fff7f4' : '#f5f7fa',
            border: `1px solid ${somAtivo ? '#D85A30' : '#d4dde8'}`,
            borderRadius: 8, padding: '7px 11px', cursor: 'pointer',
            fontSize: 15, color: somAtivo ? '#D85A30' : '#6b7a8d', transition: '.15s', flexShrink: 0,
          }}
        >{somAtivo ? '🔔' : '🔕'}</button>
        <button onClick={() => setModalNovo(true)} style={{ background: '#D85A30', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Novo Ticket</button>
      </div>

      {carregando ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 60, fontSize: 15 }}>Carregando tickets...</div>
      ) : filtroKpiInfo ? (
        <VisaoFiltrada filtroInfo={filtroKpiInfo} lista={ticketsFiltradosKpi} usuarios={usuarios}
          isGestor={isGestor} onOpen={t => setTktAberto(t)} onDelete={carregar}
          onLimpar={() => setFiltroKpi(null)} isMobile={isMobile} />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(4, 260px)' : 'repeat(4, 1fr)',
          overflowX: isMobile ? 'auto' : 'hidden',
          gap: 16,
          paddingBottom: isMobile ? 8 : 0,
          width: '100%',
          minWidth: 0,
        }}>
          {KANBAN_COLS.map(col => {
            const cols = filtrados.filter(t => col.statuses.includes(t.status));
            return (
              <KanbanColuna key={col.key} col={col} tickets={cols} usuarios={usuarios}
                isGestor={isGestor} onOpen={t => setTktAberto(t)} onDelete={carregar}
                isMobile={isMobile} />
            );
          })}
        </div>
      )}

      {tktAberto && (
        <TicketPopup ticket={tktAberto} perfis={perfis} usuarios={usuarios} tipos={tipos} categorias={categorias} mesas={mesas}
          onClose={() => { setTktAberto(null); carregar(); }}
          onUpdate={carregar}
          onDelete={() => { setTktAberto(null); carregar(); }} />
      )}

      {modalNovo && (
        <ModalNovoTicket tipos={tipos} categorias={categorias} mesas={mesas} usuarios={usuarios}
          onClose={() => setModalNovo(false)} onCriado={carregar} />
      )}
    </div>
  );
}
